import { useCallback, useMemo, useState } from 'react'
import { filterPersonalizedColorSwatches } from '~/app/utils/getColorSwatchColors'
import { getColorSwatchIdentityKey } from '~/app/features/Gallery/components/DesignTile/hooks/useColorSwatchSelection/utils'
import {
  UseColorSwatchSelectionParams,
  UseColorSwatchSelectionResult,
} from '~/app/features/Gallery/components/DesignTile/hooks/useColorSwatchSelection/interface'
import type { GalleryContentQuery } from 'shared/types/gallery/content-query'

/**
 * Resolves the currently-selected color swatch design ID for a gallery tile and exposes
 * a setter to record a new selection. Shared by `GalleryTileEntityProvider` (grid tiles)
 * and `SimilarTemplate` (Quick View "More like this" tiles).
 *
 * Tracks the selection by color identity (`getColorSwatchIdentityKey`), not raw
 * `designId`: any personalization axis change (text, image, or color) triggers a full
 * content re-fetch, and Design Conductor is not guaranteed to reuse the same `designId`
 * for the same visual color on a subsequent request. Re-resolving against the fresh
 * swatch list on every render keeps the selection intact across re-fetches; it only
 * falls back to the default swatch when that color has genuinely disappeared (e.g.
 * filtered out).
 */
export const useColorSwatchSelection = ({
  colorSwatchIds,
  designById,
  personalizationHasColor,
  fallbackSwatchId,
}: UseColorSwatchSelectionParams): UseColorSwatchSelectionResult => {
  const availableSwatches = useMemo(() => {
    const swatches = colorSwatchIds.map(designById).filter((swatch): swatch is GalleryContentQuery.ColorSwatch => Boolean(swatch))

    return personalizationHasColor ? filterPersonalizedColorSwatches(swatches, personalizationHasColor) : swatches
  }, [colorSwatchIds, designById, personalizationHasColor])

  // TODO(DIYDR-1995): This client-side initial selection override is a workaround because DC does not
  // reliably set `selectedColorSwatchDesignId` to the correct swatch when multiple personalized colors
  // are active. We pick the first entry from the filtered+sorted list so the rendered selection matches.
  // Once DIYDR-1995 is resolved and DC returns the correct default, revert this line to `fallbackSwatchId`.
  // See: https://vistaprint.atlassian.net/browse/DIYDR-1995
  const defaultSwatchId = personalizationHasColor ? (availableSwatches[0]?.designId ?? fallbackSwatchId) : fallbackSwatchId

  const [selectedSwatchKey, setSelectedSwatchKey] = useState<string | null>(null)
  const matchingSwatch = selectedSwatchKey ? availableSwatches.find((swatch) => getColorSwatchIdentityKey(swatch) === selectedSwatchKey) : undefined

  const selectSwatch = useCallback((swatch: GalleryContentQuery.ColorSwatch) => {
    setSelectedSwatchKey(getColorSwatchIdentityKey(swatch))
  }, [])

  return {
    currentDesignId: matchingSwatch?.designId ?? defaultSwatchId,
    selectSwatch,
  }
}
